import { useCallback, useMemo, useState } from 'react';
import { useLingui } from '@lingui/react/macro';

import ReviewsModal from '@rover/components/src/cards/SearchResultCard/ReviewsModal';
import { Box, Button, Flex } from '@rover/kibble/core';
import MQ from '@rover/kibble/styles/MediaQuery';
import useTheme from '@rover/kibble/themes/useTheme';
import SnappingCarousel from '@rover/react-lib/src/components/lists/Carousel/SnappingCarousel/SnappingCarousel';
import Review from '@rover/react-lib/src/components/modals/Review/Review';
import useMatchMedia from '@rover/react-lib/src/hooks/useMatchMedia';
import { useApiPersonDetailReviewsList } from '@rover/rsdk/src/apiClient/latest/api-person-detail-reviews-list';
import { ApiPersonDetailReviewsListPetType as PetTypeReview } from '@rover/rsdk/src/apiClient/latest/model/apiPersonDetailReviewsListPetType';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import useBrandSpecificPets from '@rover/shared/js/hooks/useBrandSpecificPets';
import { isCatOnlyAvailablePetTypesBasedOnPetTypes } from '@rover/utilities/getAvailablePetTypes/availablePetTypesUtils';

import PaginationWrapper from '../../../../../components/lists/Carousel/PaginationWrapper';
import { OnPaginateAction } from '../../../../../components/lists/Carousel/utils/onPaginateEvent.types';
import { OwnerConversionMemberProfileReadMoreReviewsEvent } from '../../analyticsEvents';
import { SitterProfileVersionOptions } from '../../constants';
import { useRevampProfileContext } from '../../hooks/useRevampProfileContext';
import { getInitialReviewsParams } from '../../utils/utils';
import RevampDistributionWidget from '../RevampDistributionWidget/RevampDistributionWidget';

import { SitterProfileReviewCarouselScrollEvent } from './analyticsEvents';

const CARD_WIDTH = '300px';
const CARD_HEIGHT = '64x';

const DISTRIBUTION_CARD_KEY = 'distribution';

type Props = {
  opk: string;
  sitterAvatar: string;
};

const RevampReviewsAndRatings = ({ opk, sitterAvatar }: Props): JSX.Element | null => {
  const { t } = useLingui();
  const availablePets = useBrandSpecificPets();
  const isCatOnlyBrand = isCatOnlyAvailablePetTypesBasedOnPetTypes(availablePets);
  const isMobile = useMatchMedia(MQ.XS.getBreakpointString());

  const theme = useTheme();
  const {
    sitterData,
    reviewsModalOpen: isModalOpen,
    setReviewsModalOpen: setIsModalOpen,
  } = useRevampProfileContext();
  const initialReviewsParams = useMemo(() => getInitialReviewsParams(), []);
  const [scrollIndex, setScrollIndex] = useState<number>(-1);
  const [seeMoreButtonClick, setSeeMoreButtonClick] = useState<boolean>(false);

  const { data: reviews } = useApiPersonDetailReviewsList(sitterData.opk, {
    ...initialReviewsParams,
  });

  const openModalWithCard = (index: number): void => {
    setScrollIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = (): void => {
    setIsModalOpen(false);
    setTimeout(() => setScrollIndex(-1), 200);
  };

  const handleViewAllClick = (): void => {
    emitAnalyticsEvent(
      new OwnerConversionMemberProfileReadMoreReviewsEvent({
        providerId: opk,
        pageVariant: SitterProfileVersionOptions.REVAMP,
      })
    );
    setIsModalOpen(true);
  };

  const handleCarouselPaginate = useCallback<OnPaginateAction>(
    ({ pageStart, pageEnd, scrollSource }) => {
      emitAnalyticsEvent(
        new SitterProfileReviewCarouselScrollEvent({
          direction: pageEnd > pageStart ? 'forward' : 'back',
          scrollSource,
        })
      );
    },
    []
  );

  const allItems = useMemo(() => {
    const items: Array<{ index: number; key: string; card: JSX.Element }> = [];
    items.push({
      index: -1,
      key: DISTRIBUTION_CARD_KEY,
      card: <RevampDistributionWidget isModal={false} variant="review" />,
    });

    reviews?.results.forEach((review, index) =>
      items.push({
        index,
        key: review.url,
        card: <Review review={review} showRating compact variant="profile-revamp" />,
      })
    );
    return items;
  }, [reviews?.results]);

  // Hide the section only when there is nothing to show — no text reviews AND no
  // ratings. A profile can have ratings (nonzero totalRatings) without any text
  // reviews, in which case we still render the distribution card and modal.
  if (!reviews?.results || (reviews.results.length === 0 && !reviews.totalRatings)) return null;

  const amountOfCardsToShow = 7;

  const renderCard = ({
    index,
    children,
    key,
  }: {
    index: number;
    children: React.ReactNode;
    key: string;
  }): JSX.Element => {
    const handleKeyDown = (event: React.KeyboardEvent): void => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openModalWithCard(index);
      }
    };

    return (
      <Flex
        key={key}
        minWidth={CARD_WIDTH}
        minHeight={CARD_HEIGHT}
        width={CARD_WIDTH}
        height={CARD_HEIGHT}
        flexShrink="0"
        borderRadius="8px"
        border="1px solid"
        borderColor="separator.primary"
        p="4x"
        alignItems="center"
        justifyContent="center"
        onPress={() => openModalWithCard(index)}
        role="button"
        aria-label={t`View ratings and reviews`}
        webProps={{ tabIndex: 0, onKeyDown: handleKeyDown }}
        sx={{ cursor: 'pointer' }}
      >
        {children}
      </Flex>
    );
  };

  return (
    <Box
      data-testid="revamp-reviews-and-ratings"
      width="100%"
      borderBottom="1px solid"
      borderColor={theme.colors.separator.primary}
      pb="8x"
      pt="6x"
    >
      <Box sx={{ overflowY: 'hidden' }}>
        <PaginationWrapper
          heading={t`Ratings and reviews`}
          headingVariant="static-heading"
          headingSize="400"
          hideStepperNumbers
          onPaginate={handleCarouselPaginate}
        >
          <SnappingCarousel gap="4x" position="relative">
            {allItems.slice(0, amountOfCardsToShow).map(({ index, key, card }) =>
              renderCard({
                index,
                key,
                children: card,
              })
            )}
          </SnappingCarousel>
        </PaginationWrapper>
      </Box>

      <Button mt="4x" fullWidth={isMobile} onPress={handleViewAllClick}>
        {t`View all`}
      </Button>
      <ReviewsModal
        opk={opk}
        modalOpen={isModalOpen}
        closeModal={closeModal}
        avatarUrl={sitterAvatar}
        scrollIndex={scrollIndex}
        petTypeFilter={isCatOnlyBrand ? PetTypeReview.cat : null}
        onFilterChange={undefined}
        title={t`Reviews`}
        seeMoreButtonClick={seeMoreButtonClick}
        setSeeMoreButtonClick={setSeeMoreButtonClick}
        showRating
      >
        <Box px="4x">
          <RevampDistributionWidget isModal variant="review" />
        </Box>
      </ReviewsModal>
    </Box>
  );
};

export default RevampReviewsAndRatings;
